import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { EightDReport, D2ContainmentAction } from '../types/eightd.types';

// D1: Problem Description
export const D1ProblemDescription: React.FC<{
    report: Partial<EightDReport>;
    updateField: (path: string, value: string | number | boolean | object | null | undefined) => void
}> = ({ report, updateField }) => {
    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">D1: Problem Description</h3>
                <p className="text-sm text-gray-600 mb-6">
                    Describe the problem in measurable terms using the Is/Is Not analysis
                </p>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Problem Statement *
                </label>
                <textarea
                    value={report.d1_problem?.problemStatement || ''}
                    onChange={(e) => updateField('d1_problem.problemStatement', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Clear, specific statement of the problem..."
                />
            </div>

            <div>
                <h4 className="font-medium text-gray-900 mb-4">Is / Is Not Analysis</h4>
                <div className="grid md:grid-cols-2 gap-4">
                    {['what', 'where', 'when', 'howMuch'].map((dimension) => (
                        <React.Fragment key={dimension}>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">
                                    {dimension === 'howMuch' ? 'How Much' : dimension} IS
                                </label>
                                <textarea
                                    value={report.d1_problem?.isVsIsNot?.[dimension as keyof typeof report.d1_problem.isVsIsNot]?.is || ''}
                                    onChange={(e) => updateField(`d1_problem.isVsIsNot.${dimension}.is`, e.target.value)}
                                    rows={2}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">
                                    {dimension === 'howMuch' ? 'How Much' : dimension} IS NOT
                                </label>
                                <textarea
                                    value={report.d1_problem?.isVsIsNot?.[dimension as keyof typeof report.d1_problem.isVsIsNot]?.isNot || ''}
                                    onChange={(e) => updateField(`d1_problem.isVsIsNot.${dimension}.isNot`, e.target.value)}
                                    rows={2}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                        </React.Fragment>
                    ))}
                </div>
            </div>

            <div>
                <h4 className="font-medium text-gray-900 mb-4">Quantification</h4>
                <div className="grid md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Defect Rate (%)
                        </label>
                        <input
                            type="number"
                            step="0.01"
                            value={report.d1_problem?.quantification?.defectRate || 0}
                            onChange={(e) => updateField('d1_problem.quantification.defectRate', parseFloat(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Affected Units
                        </label>
                        <input
                            type="number"
                            value={report.d1_problem?.quantification?.affectedUnits || 0}
                            onChange={(e) => updateField('d1_problem.quantification.affectedUnits', parseInt(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Financial Impact ($)
                        </label>
                        <input
                            type="number"
                            value={report.d1_problem?.quantification?.financialImpact || 0}
                            onChange={(e) => updateField('d1_problem.quantification.financialImpact', parseFloat(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                </div>
                <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Customer Impact
                    </label>
                    <textarea
                        value={report.d1_problem?.quantification?.customerImpact || ''}
                        onChange={(e) => updateField('d1_problem.quantification.customerImpact', e.target.value)}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Describe the impact on the customer..."
                    />
                </div>
            </div>
        </div>
    );
};

// D2: Containment Actions
export const D2Containment: React.FC<{
    report: Partial<EightDReport>;
    updateField: (path: string, value: string | number | boolean | object | null | undefined) => void;
    setReport: React.Dispatch<React.SetStateAction<Partial<EightDReport>>>;
}> = ({ report, setReport }) => {
    const addAction = () => {
        const newAction: D2ContainmentAction = {
            id: `ca-${Date.now()}`,
            description: '',
            responsibility: '',
            dueDate: new Date(),
            status: 'Planned',
            effectiveness: 'Under Review',
        };
        setReport(prev => ({
            ...prev,
            d2_containment: [...(prev.d2_containment || []), newAction],
        }));
    };

    const updateAction = (id: string, field: string, value: string | number | boolean | Date) => {
        setReport(prev => ({
            ...prev,
            d2_containment: prev.d2_containment?.map(action =>
                action.id === id ? { ...action, [field]: value } : action
            ),
        }));
    };

    const removeAction = (id: string) => {
        setReport(prev => ({
            ...prev,
            d2_containment: prev.d2_containment?.filter(action => action.id !== id),
        }));
    };

    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">D2: Implement Interim Containment Actions</h3>
                <p className="text-sm text-gray-600 mb-6">
                    Define and implement actions to protect customers until permanent corrective actions are in place
                </p>
            </div>

            <div className="flex justify-between items-center">
                <h4 className="font-medium text-gray-900">Containment Actions</h4>
                <button
                    onClick={addAction}
                    className="flex items-center gap-2 px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    <Plus className="w-4 h-4" />
                    Add Action
                </button>
            </div>

            {report.d2_containment && report.d2_containment.length > 0 ? (
                <div className="space-y-4">
                    {report.d2_containment.map((action, index) => (
                        <div key={action.id} className="p-4 border border-gray-200 rounded-lg">
                            <div className="flex items-start justify-between mb-4">
                                <h5 className="font-medium text-gray-900">Action #{index + 1}</h5>
                                <button
                                    onClick={() => removeAction(action.id)}
                                    className="text-red-600 hover:text-red-700"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>

                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Description *
                                    </label>
                                    <textarea
                                        value={action.description}
                                        onChange={(e) => updateAction(action.id, 'description', e.target.value)}
                                        rows={2}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Describe the containment action..."
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Responsible Person
                                    </label>
                                    <input
                                        type="text"
                                        value={action.responsibility}
                                        onChange={(e) => updateAction(action.id, 'responsibility', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Due Date
                                    </label>
                                    <input
                                        type="date"
                                        value={action.dueDate instanceof Date ? action.dueDate.toISOString().split('T')[0] : ''}
                                        onChange={(e) => updateAction(action.id, 'dueDate', new Date(e.target.value))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Status
                                    </label>
                                    <select
                                        value={action.status}
                                        onChange={(e) => updateAction(action.id, 'status', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="Planned">Planned</option>
                                        <option value="In Progress">In Progress</option>
                                        <option value="Completed">Completed</option>
                                        <option value="Verified">Verified</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Effectiveness
                                    </label>
                                    <select
                                        value={action.effectiveness}
                                        onChange={(e) => updateAction(action.id, 'effectiveness', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="Under Review">Under Review</option>
                                        <option value="Effective">Effective</option>
                                        <option value="Partially Effective">Partially Effective</option>
                                        <option value="Not Effective">Not Effective</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="p-8 bg-gray-50 border border-gray-200 rounded-lg text-center">
                    <p className="text-gray-600">No containment actions added yet</p>
                    <button
                        onClick={addAction}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Add First Action
                    </button>
                </div>
            )}
        </div>
    );
};

// Due to length, remaining components (D3-D8) will be in next file...
